import { useState, useEffect, useMemo } from 'react';
import { Trash2, Download } from 'lucide-react';
import { haalWijzigingen, wisWijzigingenApi } from '../lib/api.js';
import { useRoosterStore } from '../store/useRoosterStore.js';
import { bestandsstempelNL } from '../lib/tijd.js';

// Badge per soort — zelfde idioom als LEVEL_STIJL in LogViewer.jsx, maar met een
// leesbaar label erbij (de rauwe sleutel zegt een teamleider niets).
const SOORT_STIJL = {
  cel: { label: 'Cel gewijzigd', klasse: 'bg-blue-100 text-blue-800' },
  vastleggen: { label: 'Vastgelegd', klasse: 'bg-green-100 text-green-800' },
  ontgrendelen: { label: 'Ontgrendeld', klasse: 'bg-amber-100 text-amber-800' },
  sync: { label: 'Synchronisatie', klasse: 'bg-slate-100 text-slate-700' },
  // Server-detectie, geen gebruikersactie: vastgelegdeWeken en maandroosters tonen voor
  // deze cel verschillende namen. Rood i.p.v. amber — amber is hierboven al Ontgrendeld,
  // en dit is de error-klasse die LEVEL_STIJL in LogViewer.jsx voor 'error' gebruikt.
  // Zelfde idioom, geen nieuwe kleur.
  divergentie: { label: 'Roosters lopen uiteen', klasse: 'bg-red-100 text-red-800' },
  // Handmatig herstel door de teamleider: de vastgelegde kopie is weer gelijkgemaakt
  // aan de matrix. Emerald i.p.v. green — green is hierboven al Vastgelegd, en dit is
  // een andere handeling met een ander gevolg.
  gelijktrekken: { label: 'Rooster gelijkgetrokken', klasse: 'bg-emerald-100 text-emerald-800' },
};

// Volgorde gelijk aan WIJZ_SOORTEN in server/src/index.js, zodat filterlijst en
// server-validatie in dezelfde volgorde staan.
const SOORT_FILTERS = [
  { key: 'alle', label: 'Alle soorten' },
  { key: 'cel', label: 'Cel gewijzigd' },
  { key: 'vastleggen', label: 'Vastgelegd' },
  { key: 'ontgrendelen', label: 'Ontgrendeld' },
  { key: 'sync', label: 'Synchronisatie' },
  { key: 'divergentie', label: 'Roosters lopen uiteen' },
  { key: 'gelijktrekken', label: 'Rooster gelijkgetrokken' },
];

const CSV_KOPPEN = ['Wanneer', 'Wie', 'Wat', 'Waar', 'Details'];

function formatTijd(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso ?? '');
  return d.toLocaleString('nl-NL', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function soortLabel(soort) {
  return SOORT_STIJL[soort]?.label || soort || '?';
}

/** "week 36 (2026)" — leeg als het record geen week draagt (bv. een sync-regel). */
function weekTekst(w) {
  if (w?.weeknummer == null) return '';
  return `week ${w.weeknummer}${w.isoJaar != null ? ` (${w.isoJaar})` : ''}`;
}

/**
 * "di · Arnhem – Late dienst" — de sleutels omgezet via regels.gemeenten. Bestaat de
 * gemeente of dienst niet meer (hernoemd/verwijderd na het loggen), dan valt het terug
 * op de kale sleutel: een oud record mag nooit leeg of misleidend worden.
 */
function plaatsTekst(w, regels) {
  const delen = [];
  if (w?.dag) delen.push(w.dag);
  if (w?.gemeenteKey || w?.dienstId) {
    const gem = regels?.gemeenten?.[w.gemeenteKey];
    const gemNaam = gem?.naam || w.gemeenteKey || '?';
    const dienst = gem?.diensten?.[w.dienstId];
    const dienstNaam = dienst?.label || w.dienstId;
    delen.push(dienstNaam ? `${gemNaam} – ${dienstNaam}` : gemNaam);
  }
  return delen.join(' · ');
}

/** Bij een cel-bewerking de leesbare zin; bij de rest het vrije detail-veld. */
function detailTekst(w) {
  if (w?.soort === 'cel' && w?.wieNaam) {
    if (w.operatie === 'toevoegen') return `${w.wieNaam} toegevoegd`;
    if (w.operatie === 'weghalen') return `${w.wieNaam} weggehaald`;
    return w.wieNaam;
  }
  return w?.detail || '';
}

/** CSV-veld: quoten zodra er een puntkomma, aanhalingsteken of regeleinde in zit. */
function csvVeld(waarde) {
  const s = String(waarde ?? '');
  return /[";\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export default function WijzigingViewer() {
  const regels = useRoosterStore((s) => s.regels);
  const [entries, setEntries] = useState([]);
  const [soort, setSoort] = useState('alle');
  const [persoon, setPersoon] = useState('alle');
  const [laden, setLaden] = useState(true);
  const [fout, setFout] = useState(null);
  const [herlaad, setHerlaad] = useState(0); // ophogen = opnieuw laden (Vernieuwen / na Wis)

  // Server is de bron: dit toont de wijzigingen van ALLE teamleiders (teamleider-only
  // endpoint). Idioom = LogViewer.jsx: de async laad-functie staat IN het effect.
  useEffect(() => {
    let geannuleerd = false;
    const laad = async () => {
      setLaden(true);
      setFout(null);
      try {
        const res = await haalWijzigingen();
        if (!geannuleerd) setEntries(Array.isArray(res?.wijzigingen) ? res.wijzigingen : []);
      } catch (err) {
        if (!geannuleerd) setFout(err?.message || 'Het wijzigingslogboek kon niet worden geladen.');
      } finally {
        if (!geannuleerd) setLaden(false);
      }
    };
    laad();
    return () => { geannuleerd = true; };
  }, [herlaad]);

  // Personen-filter vult zich uit de OPGEHAALDE lijst, niet uit de medewerker-config:
  // iemand die inmiddels uit de config is verdwenen staat wel nog in oude records.
  const personen = useMemo(() => {
    const namen = new Set();
    for (const e of entries) if (e?.naam) namen.add(e.naam);
    return [...namen].sort((a, b) => a.localeCompare(b, 'nl-NL'));
  }, [entries]);

  // De server levert al nieuwste-eerst; hier alleen nog filteren (zoals LogViewer).
  const zichtbaar = useMemo(() => entries.filter((e) =>
    (soort === 'alle' || e?.soort === soort) && (persoon === 'alle' || e?.naam === persoon)
  ), [entries, soort, persoon]);

  async function wis() {
    if (!window.confirm('Het wijzigingslogboek van ALLE teamleiders echt wissen? Dit kan niet ongedaan worden gemaakt.')) return;
    setFout(null);
    try {
      await wisWijzigingenApi();
      setHerlaad((n) => n + 1); // opnieuw laden toont de nu-lege lijst
    } catch (err) {
      setFout(err?.message || 'Wissen is mislukt.');
    }
  }

  // Exporteer wat er NU zichtbaar is (de gefilterde lijst) als CSV voor Excel-NL:
  // puntkomma als scheidingsteken, CRLF als regeleinde en een BOM zodat Excel de
  // accenten en het – teken goed toont.
  function exporteerCsv() {
    try {
      const rijen = zichtbaar.map((w) => [
        formatTijd(w?.tijdstip),
        w?.naam ?? '',
        soortLabel(w?.soort),
        [weekTekst(w), plaatsTekst(w, regels)].filter(Boolean).join(' · '),
        detailTekst(w),
      ].map(csvVeld).join(';'));
      // \uFEFF als ESCAPE, niet als letterlijk teken: een echte BOM in de broncode is
      // onzichtbaar en wordt door eslint (no-irregular-whitespace) terecht geweigerd.
      const inhoud = `\uFEFF${[CSV_KOPPEN.join(';'), ...rijen].join('\r\n')}`;
      // bestandsstempelNL, niet toISOString: die noemt een export van 00:05 naar gisteren
      // en zet er het UTC-uur bij — zie lib/tijd.js.
      const stamp = bestandsstempelNL();
      const blob = new Blob([inhoud], { type: 'text/csv;charset=utf-8' });
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = `sass-rooster-wijzigingen-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch {
      setFout('Exporteren is mislukt.');
    }
  }

  return (
    <div className="space-y-4">
      {/* Actiebalk: filters links, knoppen rechts */}
      <div className="flex justify-between items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={soort}
            onChange={(e) => setSoort(e.target.value)}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          >
            {SOORT_FILTERS.map((f) => (
              <option key={f.key} value={f.key}>{f.label}</option>
            ))}
          </select>
          <select
            value={persoon}
            onChange={(e) => setPersoon(e.target.value)}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          >
            <option value="alle">Alle personen</option>
            {personen.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => setHerlaad((n) => n + 1)}
            className="px-3 py-2 text-xs text-slate-500 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 hover:text-slate-700 transition"
          >
            Vernieuwen
          </button>
          <button
            onClick={exporteerCsv}
            className="flex items-center gap-1.5 px-3 py-2 text-xs text-slate-500 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 hover:text-slate-700 transition"
          >
            <Download className="w-3.5 h-3.5" />
            Exporteer CSV
          </button>
          <button
            onClick={wis}
            className="flex items-center gap-1.5 px-3 py-2 text-xs text-slate-500 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 hover:text-red-600 transition"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Wis logboek
          </button>
        </div>
      </div>

      {/* Foutregel — nooit stil wegslikken */}
      {fout && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
          {fout}
        </div>
      )}

      {/* Tabel / laad- / lege staat */}
      {laden ? (
        <div className="text-center py-8 text-slate-400 text-sm">Wijzigingen laden…</div>
      ) : zichtbaar.length === 0 ? (
        <div className="text-center py-8 text-slate-400 text-sm">
          {entries.length === 0 ? 'Er zijn nog geen wijzigingen vastgelegd.' : 'Geen wijzigingen voor dit filter.'}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs text-slate-400">
                <th className="px-4 py-3 font-medium whitespace-nowrap">Wanneer</th>
                <th className="px-4 py-3 font-medium whitespace-nowrap">Wie</th>
                <th className="px-4 py-3 font-medium whitespace-nowrap">Wat</th>
                <th className="px-4 py-3 font-medium">Waar</th>
                <th className="px-4 py-3 font-medium">Details</th>
              </tr>
            </thead>
            <tbody>
              {zichtbaar.map((w) => {
                const stijl = SOORT_STIJL[w?.soort];
                const week = weekTekst(w);
                const plaats = plaatsTekst(w, regels);
                return (
                  <tr key={w.id} className="border-b border-slate-100 last:border-0 align-top">
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{formatTijd(w?.tijdstip)}</td>
                    <td className="px-4 py-3 text-slate-700 font-medium whitespace-nowrap">{w?.naam || '—'}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${stijl?.klasse || SOORT_STIJL.sync.klasse}`}>
                        {soortLabel(w?.soort)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {week || plaats ? (
                        <>
                          {week && <div className="whitespace-nowrap">{week}</div>}
                          {plaats && <div className="text-xs text-slate-400">{plaats}</div>}
                        </>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3 text-slate-600 break-words">{detailTekst(w) || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
